import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  createTableColumn,
  useTableFeatures,
  useTableColumnSizing_unstable,
} from "@fluentui/react-components";
import TableHeaderContainer from "../../../Containers/RecordingContainer/TableContainer/TableHeaderContainer";
import TableBodyContainer from "../../../Containers/RecordingContainer/TableContainer/TableBodyContainer";
import "./CSS/TableComponent.css";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";

const TableComponent = ({ data, filters, onFilterChange }) => {
  const [filteredItems, setFilteredItems] = useState([]);
  const [isAudioPlayed, setIsAudioPlayed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15); // Par défaut

 // ⚡ Dynamically calculate rowsPerPage based on window height, without scroll and with pagination visible
const calculateRowsPerPage = () => {
  const windowHeight = window.innerHeight;
  // Récupérer la hauteur du conteneur de la pagination (l'espace réservé pour la pagination)
  const paginationContainer = document.querySelector(".pagination-container");
  const paginationHeight = paginationContainer ? paginationContainer.offsetHeight : 0;

  // Calculer l'espace disponible pour le tableau (exclure la pagination)
  const availableHeight = windowHeight - paginationHeight;

  const rowHeight = 40; // Ajuste selon la vraie hauteur des lignes

  // Calculer combien de lignes peuvent tenir dans l'espace disponible
  const calculatedRows = Math.floor(availableHeight / rowHeight);

  // S'assurer qu'il y a toujours un minimum de 5 lignes
  setRowsPerPage(calculatedRows > 5 ? calculatedRows : 5);
};

useEffect(() => {
  calculateRowsPerPage();
  window.addEventListener("resize", calculateRowsPerPage); // Recalculer lors du redimensionnement

  return () => window.removeEventListener("resize", calculateRowsPerPage);
}, []);

  

  // Colonnes
  const columns = useMemo(
    () => [
      createTableColumn({ columnId: "Recording" }),
      createTableColumn({ columnId: "Direction" }),
      createTableColumn({ columnId: "recordingScope" }),
      createTableColumn({ columnId: "callerId" }),
      createTableColumn({ columnId: "duration" }),
      createTableColumn({ columnId: "agentName" }),
      createTableColumn({ columnId: "date" }),
      createTableColumn({ columnId: "callTrancription" }),
      createTableColumn({ columnId: "callId" }),
    ],
    []
  );

  // Calcul du width
  const calculateColumnWidth = (columnId, data) => {
    if(data && data.length > 0){
      let maxLength = Math.max(
        ...data.map((item) => (item[columnId] ? item[columnId].length : 0)),
        columnId.length
      );
      return Math.max(maxLength * 10, 150);
    }

  };

  const columnSizingOptions = useMemo(() => {
    const columns = [
      "Recording",
      "Direction",
      "recordingScope",
      "callerId",
      "duration",
      "agentName",
      "date",
      "callTrancription",
      "callId",
    ];

    const sizingOptions = {};
    columns.forEach((column) => {
      sizingOptions[column] = {
        defaultWidth: Math.max(calculateColumnWidth(column, data), 100),
      };
    });
    return sizingOptions;
  }, [data]);

  const { getRows, columnSizing_unstable, tableRef } = useTableFeatures(
    { columns, items: filteredItems },
    [
      useTableColumnSizing_unstable({
        columnSizingOptions,
        autoFitColumns: false,
      }),
    ]
  );

  // Pagination handler
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Pagination logique
  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const rows = getRows();
  const currentRows = rows.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="table-container">
      <div className="scroll-table">
        <Table
          ref={tableRef}
          {...columnSizing_unstable.getTableProps()}
          noNativeElements={true}
          size="small"
          aria-label="Filtered Recordings Table"
        >
          <TableHeaderContainer
       
            onFilterChange={onFilterChange}
            filters={filters}
            isAudioPlayed={isAudioPlayed}
            columnSizing_unstable={columnSizing_unstable}
          />
          <TableBodyContainer
            data={data}
            setIsAudioPlayed={setIsAudioPlayed}
            isAudioPlayed={isAudioPlayed}
            setFilteredItems={setFilteredItems}
            filteredItems={filteredItems}
            currentRows={currentRows}
            columnSizing_unstable={columnSizing_unstable}
          />
        </Table>
      </div>
      <Stack spacing={2} alignItems="center" className="pagination-container" >
        <Pagination
          count={Math.ceil(filteredItems.length / rowsPerPage)}
          color="primary"
          page={currentPage}
          onChange={handlePageChange}
          variant="outlined"
          shape="rounded"
        />
      </Stack>
    </div>
  );
};

export default TableComponent;
